import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn, easeOutCubic } from '../../utils';
import { Button } from '../Button';
import { CrossIcon } from '../Icons';
import { Text } from '../Text';

export const Dialog = ({
  children,
  maxHeight = 520,
  maxWidth = 560,
  isOpen,
}: {
  children: React.ReactNode;
  maxHeight?: number;
  maxWidth?: number;
  isOpen: boolean;
}) => {
  const animationVariants = {
    initial: {
      scale: 0.9,
    },
    animate: {
      scale: isOpen ? 1 : 0.9,
      transition: {
        duration: 0.2,
        ease: easeOutCubic,
      },
    },
    exit: {
      scale: 0.9,
      transition: {
        duration: 0.1,
        ease: easeOutCubic,
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={animationVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="flex flex-col w-full h-full bg-white rounded-t-2xl sm:rounded-2xl"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: `${maxHeight}px`, maxWidth: `${maxWidth}px` }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const DialogContent = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="overflow-auto pb-4 px-4 sm:pb-6 sm:px-6 flex-1">
      <div className="grid grid-cols-1 gap-4 sm:gap-6">{children}</div>
    </div>
  );
};

export const DialogHeader = ({
  title,
  handleClose,
  variant = 'default',
}: {
  title: string;
  handleClose: () => void;
  variant?: 'minimal' | 'default';
}) => {
  if (variant === 'minimal') {
    return (
      <div className="sticky top-0 flex justify-center items-center py-4 px-4 sm:px-6 border-b">
        <div className="w-8" />
        <Text variant="Body" className="text-center w-full">
          {title}
        </Text>
        <div
          className="flex items-center justify-center w-8 h-8 cursor-pointer"
          onClick={handleClose}
        >
          <CrossIcon className="w-6 h-6 sm:w-8 sm:h-8" />
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 flex justify-between items-center p-4 sm:p-6">
      <Text variant="H2" sm="Title3">
        {title}
      </Text>
      <div
        className="flex items-center justify-center w-8 h-8 cursor-pointer"
        onClick={handleClose}
      >
        <CrossIcon className="w-6 h-6 sm:w-8 sm:h-8" />
      </div>
    </div>
  );
};

export const DialogFooter = ({
  handleClose,
  children,
  className,
}: {
  handleClose?: () => void;
  children?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        'sm:rounded-b-2xl sticky bg-white bottom-0 px-4 sm:px-6 py-3 flex justify-end border-t border-t-[#F2F2F2]',
        className,
      )}
    >
      {children ? (
        children
      ) : (
        <Button variant="primary" onClick={handleClose}>
          Done
        </Button>
      )}
    </div>
  );
};
